import {
  Badge,
  Button,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@veritrellis/ui";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { usePersistedAdminEnvironment } from "../hooks/usePersistedAdminEnvironment.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";
import {
  envLabel,
  formatDateTimeShort,
  formatRequestStatus,
  statusBadgeVariant
} from "../lib/requestPresentation.js";

type RequestRow = {
  id: string;
  action_type: string;
  status: string;
  created_at: string;
};

type StatusFilter =
  | "all"
  | "pending_approval"
  | "allowed"
  | "denied"
  | "approved"
  | "rejected"
  | "expired";

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "pending_approval", label: "Pending" },
  { value: "allowed", label: "Allowed" },
  { value: "denied", label: "Denied" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "expired", label: "Expired" }
];

export const RequestsListPage = () => {
  const navigate = useNavigate();
  const { workspaceId, isLoading } = useWorkspaceId();
  const [environment] = usePersistedAdminEnvironment();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const q = useQuery({
    queryKey: ["requests", workspaceId, environment],
    enabled: workspaceId !== null,
    queryFn: async () => {
      const res = await apiFetch(
        `/admin/workspaces/${workspaceId}/requests?environment=${environment}&limit=50`
      );
      if (!res.ok) {
        throw new Error("requests_failed");
      }
      return (await res.json()) as { requests: RequestRow[] };
    }
  });

  const rows = q.data?.requests ?? [];

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) {
        return false;
      }
      if (term.length === 0) {
        return true;
      }
      return (
        r.id.toLowerCase().includes(term) ||
        r.action_type.toLowerCase().includes(term) ||
        r.status.toLowerCase().includes(term)
      );
    });
  }, [rows, search, statusFilter]);

  const counts = useMemo(() => {
    const pending = rows.filter((r) => r.status === "pending_approval").length;
    const allowed = rows.filter((r) => r.status === "allowed" || r.status === "approved").length;
    const denied = rows.filter(
      (r) => r.status === "denied" || r.status === "rejected" || r.status === "expired"
    ).length;
    return { total: rows.length, pending, allowed, denied };
  }, [rows]);

  if (isLoading) {
    return (
      <div className="admin-page-stack">
        <Skeleton style={{ height: 48, width: "40%" }} />
        <Skeleton style={{ height: 320, width: "100%" }} />
      </div>
    );
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div className="admin-page-stack">
      <section className="admin-panel" aria-labelledby="requests-table-heading">
        <div className="admin-panel-header">
          <h2 id="requests-table-heading" className="admin-panel-title">
            All requests
          </h2>
          {counts.pending > 0 ? (
            <Link to="/approvals" className="admin-panel-link">
              {counts.pending} pending approval
            </Link>
          ) : null}
        </div>

        <div className="admin-filter-toolbar">
          <div className="admin-filter-pills" role="group" aria-label="Filter by status">
            {STATUS_FILTERS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={`admin-filter-pill${statusFilter === value ? " admin-filter-pill-active" : ""}`}
                onClick={() => setStatusFilter(value)}
                aria-pressed={statusFilter === value}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="admin-search-field">
            <label className="admin-search-label" htmlFor="requests-search">
              Search
            </label>
            <Input
              id="requests-search"
              type="search"
              placeholder="ID, action, or status…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
          </div>
        </div>

        {!q.isLoading && rows.length > 0 ? (
          <div className="admin-count-strip">
            <span>
              Showing <strong>{filtered.length}</strong> of <strong>{counts.total}</strong>
            </span>
            {counts.pending > 0 ? (
              <span>
                <strong>{counts.pending}</strong> pending
              </span>
            ) : null}
            <span>
              <strong>{counts.allowed}</strong> allowed
            </span>
            <span>
              <strong>{counts.denied}</strong> denied / rejected
            </span>
          </div>
        ) : null}

        <div className="admin-panel-body">
          {q.isLoading ? (
            <div className="admin-panel-body-padded">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} style={{ height: 40, width: "100%", marginBottom: 8 }} />
              ))}
            </div>
          ) : q.isError ? (
            <div className="admin-panel-body-padded">
              <p style={{ margin: 0, fontSize: 14, color: "var(--destructive)" }}>
                Failed to load requests. Try refreshing the page.
              </p>
            </div>
          ) : rows.length === 0 ? (
            <div className="admin-empty-state">
              <div className="admin-empty-state-icon">
                <ClipboardList style={{ width: 22, height: 22 }} />
              </div>
              <p className="admin-empty-state-text">No requests in {envLabel(environment)} yet</p>
              <Button asChild variant="secondary" size="sm">
                <Link to="/policies">Configure policies</Link>
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="admin-empty-state">
              <p className="admin-empty-state-text">No requests match your filters</p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setSearch("");
                  setStatusFilter("all");
                }}
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={{ paddingLeft: 20 }}>Request</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead style={{ textAlign: "right", paddingRight: 20 }}>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow
                    key={r.id}
                    className="admin-table-row-interactive"
                    onClick={() => navigate(`/requests/${r.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/requests/${r.id}`);
                      }
                    }}
                    tabIndex={0}
                    role="link"
                    aria-label={`View request ${r.action_type}`}
                  >
                    <TableCell style={{ paddingLeft: 20 }}>
                      <code style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                        {r.id.slice(0, 8)}…
                      </code>
                    </TableCell>
                    <TableCell>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>{r.action_type}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(r.status)}>
                        {formatRequestStatus(r.status)}
                      </Badge>
                    </TableCell>
                    <TableCell style={{ textAlign: "right", paddingRight: 20 }}>
                      <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                        {formatDateTimeShort(r.created_at)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </section>
    </div>
  );
};
